/**
 * @file Post FX: the debug hub's tab for the pulse-driven grade
 * (`PostProcessing.js` / `PulseEffect.js`). Sliders write straight into
 * `ctx.config.post.pulse`, which the composer already reads every frame, so
 * unlike the Levels tab there is no "apply" step — moving a slider is live.
 */

/**
 * Range-driven fields on `config.post.pulse`. Each renders a min/max pair of
 * sliders plus a live numeric readout.
 * @type {readonly { key: string, label: string, min: number, max: number, step: number, decimals: number }[]}
 */
const RANGE_FIELDS = Object.freeze([
  {
    key: 'aberration',
    label: 'Chromatic aberration',
    min: 0,
    max: 0.01,
    step: 0.0001,
    decimals: 4
  },
  { key: 'glow', label: 'Glow (bloom strength)', min: 0, max: 3, step: 0.01, decimals: 2 },
  { key: 'saturation', label: 'Saturation', min: 0, max: 2.5, step: 0.01, decimals: 2 }
]);

export class PostFxPanel {
  /**
   * @param {HTMLElement} host Tab body handed out by the debug window.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   */
  constructor(host, ctx) {
    /** @type {import('../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {HTMLElement} @private */
    this._host = host;
    this._build();
  }

  /** @returns {void} @private */
  _build() {
    const pulse = this._ctx.config.post.pulse;

    const fieldSection = (field) => `
      <section class="level-lab__section">
        <h4 class="level-lab__heading">${field.label}
          <span class="level-lab__hint">min <span data-pulse-readout="${field.key}.min">--</span> · max <span data-pulse-readout="${field.key}.max">--</span></span>
        </h4>
        <input type="range" min="${field.min}" max="${field.max}" step="${field.step}"
          data-pulse-range="${field.key}.min" class="level-lab__slider" />
        <input type="range" min="${field.min}" max="${field.max}" step="${field.step}"
          data-pulse-range="${field.key}.max" class="level-lab__slider" />
      </section>`;

    this._host.innerHTML = `
      <div class="level-lab">
        <section class="level-lab__section">
          <h4 class="level-lab__heading">Pulse</h4>
          <label class="level-lab__check">
            <input type="checkbox" data-pulse="enabled" />
            <span>Enable pulse effect (kill-timer ratio drives aberration/glow/saturation)</span>
          </label>
          <div class="level-lab__row">
            <label>Smoothing
              <input type="number" data-pulse="smoothing" min="0.1" max="30" step="0.1" />
            </label>
          </div>
        </section>
        ${RANGE_FIELDS.map(fieldSection).join('')}
      </div>
    `;

    const el = (q) => /** @type {HTMLElement} */ (this._host.querySelector(q));
    this._els = {
      enabled: /** @type {HTMLInputElement} */ (el('[data-pulse="enabled"]')),
      smoothing: /** @type {HTMLInputElement} */ (el('[data-pulse="smoothing"]'))
    };

    this._els.enabled.checked = Boolean(pulse.enabled);
    this._els.smoothing.value = String(pulse.smoothing);
    for (const field of RANGE_FIELDS) {
      this._setRangeInput(field.key, 'min', pulse[field.key].min, field.decimals);
      this._setRangeInput(field.key, 'max', pulse[field.key].max, field.decimals);
    }

    this._els.enabled.addEventListener('change', () => {
      pulse.enabled = this._els.enabled.checked;
    });
    this._els.smoothing.addEventListener('input', () => {
      pulse.smoothing = Number(this._els.smoothing.value);
    });

    // One delegated listener for every min/max slider: the set is static, but
    // this keeps future range fields wired for free (mirrors DebugPanel.js's
    // single delegated toggle listener).
    this._host.addEventListener('input', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      const spec = input.dataset?.pulseRange;
      if (!spec) return;
      const [key, bound] = spec.split('.');
      const field = RANGE_FIELDS.find((candidate) => candidate.key === key);
      if (!field) return;
      pulse[key][bound] = Number(input.value);
      this._setReadout(key, bound, pulse[key][bound], field.decimals);
    });
  }

  /**
   * Sets a slider's value and syncs its readout in one call.
   * @param {string} key Field key (`aberration`, `glow`, `saturation`).
   * @param {'min' | 'max'} bound Which end of the range.
   * @param {number} value Value to display and set on the slider.
   * @param {number} decimals Digits after the decimal point for the readout.
   * @returns {void}
   * @private
   */
  _setRangeInput(key, bound, value, decimals) {
    const input = /** @type {HTMLInputElement | null} */ (
      this._host.querySelector(`[data-pulse-range="${key}.${bound}"]`)
    );
    if (input) input.value = String(value);
    this._setReadout(key, bound, value, decimals);
  }

  /**
   * @param {string} key Field key.
   * @param {'min' | 'max'} bound Which end of the range.
   * @param {number} value Value to display.
   * @param {number} decimals Digits after the decimal point.
   * @returns {void}
   * @private
   */
  _setReadout(key, bound, value, decimals) {
    const node = this._host.querySelector(`[data-pulse-readout="${key}.${bound}"]`);
    if (node) node.textContent = value.toFixed(decimals);
  }

  /** @returns {void} Removes this tab's content. */
  dispose() {
    this._host.innerHTML = '';
  }
}
